import { useState, useEffect, useRef, useCallback } from 'react';
import { isMobileDevice, isMac } from '../../utils/deviceDetection';
import { useAuth } from '../../contexts/AuthContext';
import { autoCropReceipt } from './receiptCrop';
import { CropReview } from './CropReview';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import { Camera, Upload, X, FileText, Plus, Clipboard } from 'lucide-react';

const ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'image/heic', 'image/heif', 'image/webp', 'application/pdf'];
const ACCEPTED_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.heic', '.heif', '.webp', '.pdf'];
const MAX_FILES = 5;
const MAX_TOTAL_SIZE = 25 * 1024 * 1024;

function isValidFile(file) {
  const ext = '.' + (file.name || '').split('.').pop().toLowerCase();
  return ACCEPTED_TYPES.includes(file.type) || ACCEPTED_EXTENSIONS.includes(ext);
}

async function compressImage(file) {
  if (file.type === 'application/pdf' || file.type?.includes('heic') || file.type?.includes('heif')) {
    return file;
  }
  if (!file.type?.startsWith('image/')) return file;

  return new Promise((resolve) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      const MAX_DIM = 2000;
      let { width, height } = img;
      if (width <= MAX_DIM && height <= MAX_DIM) {
        resolve(file);
        return;
      }
      const ratio = Math.min(MAX_DIM / width, MAX_DIM / height);
      width = Math.round(width * ratio);
      height = Math.round(height * ratio);

      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, width, height);
      canvas.toBlob(
        (blob) => {
          if (blob) {
            const compressed = new File([blob], file.name || 'image.jpg', { type: 'image/jpeg' });
            resolve(compressed);
          } else {
            resolve(file);
          }
        },
        'image/jpeg',
        0.85
      );
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      resolve(file);
    };
    img.src = url;
  });
}

function FilePreview({ file }) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    const u = URL.createObjectURL(file);
    setUrl(u);
    return () => URL.revokeObjectURL(u);
  }, [file]);
  return url ? <img src={url} alt="" className="w-full h-full object-cover" /> : null;
}

export function CaptureInput({ files, setFiles, onContinue, onBusy }) {
  const { api } = useAuth();
  const [isDragging, setIsDragging] = useState(false);
  const [pending, setPending] = useState([]);
  const [selected, setSelected] = useState(0);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const mobile = isMobileDevice();
  const pasteKey = isMac() ? '⌘V' : 'Ctrl+V';

  const processing = pending.length > 0;
  useEffect(() => { onBusy?.(processing); }, [processing, onBusy]);
  const sel = Math.min(selected, Math.max(0, files.length - 1));

  const totalSize = files.reduce((acc, f) => acc + (f.size || 0), 0);

  const addFiles = useCallback(async (newFiles) => {
    const validFiles = [];
    for (const file of newFiles) {
      if (!isValidFile(file)) {
        toast.error(`${file.name || 'File'}: unsupported type. Use images or PDFs.`);
        continue;
      }
      validFiles.push(file);
    }

    if (files.length + validFiles.length > MAX_FILES) {
      toast.error(`Maximum ${MAX_FILES} files allowed`);
      validFiles.splice(MAX_FILES - files.length);
    }

    const newTotal = totalSize + validFiles.reduce((a, f) => a + (f.size || 0), 0);
    if (newTotal > MAX_TOTAL_SIZE) {
      toast.error('Total file size exceeds 25MB limit');
      return;
    }

    if (validFiles.length === 0) return;

    const compressed = await Promise.all(validFiles.map(compressImage));
    setPending(compressed);
    // Trim the background off receipt photos; the original is always kept as the alternative.
    const chosen = await Promise.all(compressed.map(f => autoCropReceipt(api, f)));
    setSelected(files.length);
    setFiles(prev => [...prev, ...chosen]);
    setPending([]);
  }, [files.length, totalSize, setFiles, api]);

  const swapFile = (from, to) => setFiles(prev => prev.map(f => (f === from ? to : f)));

  // Clipboard paste handler
  useEffect(() => {
    const handlePaste = (e) => {
      const items = e.clipboardData?.items;
      if (!items) return;
      const pastedFiles = [];
      for (const item of items) {
        if (item.kind === 'file') {
          const file = item.getAsFile();
          if (file) pastedFiles.push(file);
        }
      }
      if (pastedFiles.length > 0) {
        e.preventDefault();
        addFiles(pastedFiles);
      }
    };
    window.addEventListener('paste', handlePaste);
    return () => window.removeEventListener('paste', handlePaste);
  }, [addFiles]);

  const handleDragOver = (e) => { e.preventDefault(); setIsDragging(true); };
  const handleDragLeave = (e) => { e.preventDefault(); setIsDragging(false); };
  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const droppedFiles = Array.from(e.dataTransfer.files);
    addFiles(droppedFiles);
  };

  const handleFileSelect = (e) => {
    const selected = Array.from(e.target.files || []);
    addFiles(selected);
    e.target.value = '';
  };

  const removeFile = (index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const renderThumbnail = (file, index) => {
    const isPdf = file.type === 'application/pdf' || file.name?.endsWith('.pdf');
    return (
      <div key={index} role="button" tabIndex={0} onClick={() => setSelected(index)} onKeyDown={(e) => e.key === 'Enter' && setSelected(index)}
        className={`relative group w-20 h-20 rounded-lg overflow-hidden border bg-[#1E2530] flex-shrink-0 cursor-pointer transition-colors ${index === sel ? 'border-[#F5A623]' : 'border-white/10'}`} data-testid={`file-thumb-${index}`}>
        {isPdf ? (
          <div className="w-full h-full flex flex-col items-center justify-center">
            <FileText className="w-8 h-8 text-red-400" />
            <span className="text-[9px] text-gray-400 mt-0.5 truncate max-w-[70px]">PDF</span>
          </div>
        ) : (
          <FilePreview file={file} />
        )}
        <button
          onClick={(e) => { e.stopPropagation(); removeFile(index); }}
          className="absolute top-0.5 right-0.5 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center opacity-70 group-hover:opacity-100 transition-opacity"
          data-testid={`remove-file-${index}`}
        >
          <X className="w-3 h-3 text-white" />
        </button>
      </div>
    );
  };

  const reviewPanel = files.length > 0
    ? <CropReview file={files[sel]} busy={false} onSwap={swapFile} />
    : processing ? <CropReview file={pending[0]} busy onSwap={() => {}} /> : null;

  // === MOBILE LAYOUT ===
  if (mobile) {
    return (
      <div className="space-y-4 px-4" data-testid="capture-input-mobile">
        {files.length === 0 && !processing ? (
          <>
            <button
              onClick={() => cameraInputRef.current?.click()}
              className="w-full py-16 rounded-2xl bg-[#F5A623] text-[#1E2530] font-bold text-lg flex flex-col items-center gap-3 active:scale-[0.98] transition-transform"
              data-testid="take-photo-btn"
            >
              <Camera className="w-12 h-12" />
              Take Photo
            </button>
            <button
              onClick={() => fileInputRef.current?.click()}
              className="w-full py-4 rounded-xl bg-[#2A3240] border border-white/10 text-gray-300 font-medium flex items-center justify-center gap-2 active:scale-[0.98]"
              data-testid="choose-gallery-btn"
            >
              <Upload className="w-5 h-5" />
              Choose from gallery or files
            </button>
          </>
        ) : (
          <>
            {reviewPanel}
            <div className="flex gap-2 overflow-x-auto py-2">
              {files.map(renderThumbnail)}
              {files.length < MAX_FILES && (
                <button
                  onClick={() => cameraInputRef.current?.click()}
                  className="w-20 h-20 rounded-lg border-2 border-dashed border-white/20 flex flex-col items-center justify-center text-gray-400 flex-shrink-0"
                  data-testid="add-more-btn"
                >
                  <Plus className="w-6 h-6" />
                  <span className="text-[9px]">Add</span>
                </button>
              )}
            </div>
            <p className="text-xs text-gray-400 text-center">{files.length} of {MAX_FILES} files</p>
          </>
        )}

        <input ref={cameraInputRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={handleFileSelect} />
        <input ref={fileInputRef} type="file" accept={ACCEPTED_EXTENSIONS.join(',')} multiple className="hidden" onChange={handleFileSelect} />

        {processing && <p className="text-center text-sm text-[#F5A623] animate-pulse" data-testid="capture-trimming">Trimming the background…</p>}

        {files.length > 0 && !processing && (
          <Button onClick={onContinue} className="w-full py-6 text-lg bg-[#F5A623] text-[#1E2530] hover:bg-[#D98E1D] font-bold rounded-xl" data-testid="continue-btn">
            Continue
          </Button>
        )}
      </div>
    );
  }

  // === DESKTOP LAYOUT ===
  return (
    <div className="space-y-4" data-testid="capture-input-desktop">
      {files.length === 0 && !processing ? (
        <>
          <div
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            tabIndex={0}
            className={`min-h-[400px] rounded-2xl border-2 border-dashed transition-all flex flex-col items-center justify-center gap-4 outline-none focus:border-[#F5A623]/50 ${
              isDragging ? 'border-[#F5A623] bg-[#F5A623]/5' : 'border-white/20 bg-[#2A3240]/50'
            }`}
            data-testid="drop-zone"
          >
            <Upload className={`w-16 h-16 ${isDragging ? 'text-[#F5A623]' : 'text-gray-400'}`} />
            <div className="text-center">
              <p className="text-white text-lg font-medium">
                {isDragging ? 'Drop files here' : `Drop files here or paste with ${pasteKey}`}
              </p>
              <p className="text-gray-400 text-sm mt-1">Images and PDFs accepted, up to {MAX_FILES} files (25MB max)</p>
            </div>
            <div className="flex items-center gap-2 mt-2 px-3 py-1.5 rounded-lg bg-white/5 border border-white/10">
              <Clipboard className="w-4 h-4 text-gray-400" />
              <span className="text-xs text-gray-400">Tip: paste screenshots with {pasteKey}</span>
            </div>
          </div>
          <div className="flex justify-center">
            <button
              onClick={() => fileInputRef.current?.click()}
              className="px-5 py-2 rounded-lg border border-white/20 text-gray-300 text-sm font-medium hover:bg-white/5 hover:border-white/30 transition-colors"
              data-testid="choose-files-btn"
            >
              Choose Files
            </button>
          </div>
        </>
      ) : (
        <>
          {reviewPanel}
          <div
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            className={`rounded-xl border border-white/10 p-6 ${isDragging ? 'border-[#F5A623] bg-[#F5A623]/5' : 'bg-[#2A3240]/50'}`}
          >
            <div className="flex flex-wrap gap-3">
              {files.map(renderThumbnail)}
              {files.length < MAX_FILES && (
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="w-20 h-20 rounded-lg border-2 border-dashed border-white/20 flex flex-col items-center justify-center text-gray-400 hover:border-[#F5A623] hover:text-[#F5A623] transition-colors"
                  data-testid="add-more-btn"
                >
                  <Plus className="w-6 h-6" />
                  <span className="text-[9px]">Add</span>
                </button>
              )}
            </div>
            <p className="text-xs text-gray-400 mt-3">{files.length} of {MAX_FILES} files | Drop more or {pasteKey} to paste</p>
          </div>
        </>
      )}

      <input ref={fileInputRef} type="file" accept={ACCEPTED_EXTENSIONS.join(',')} multiple className="hidden" onChange={handleFileSelect} />

      {processing && <p className="text-center text-sm text-[#F5A623] animate-pulse" data-testid="capture-trimming">Trimming the background…</p>}

      {files.length > 0 && !processing && (
        <Button onClick={onContinue} className="w-full py-4 text-base bg-[#F5A623] text-[#1E2530] hover:bg-[#D98E1D] font-bold rounded-xl" data-testid="continue-btn">
          Continue with {files.length} file{files.length > 1 ? 's' : ''}
        </Button>
      )}
    </div>
  );
}
